import React, { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { nullable } from '../../../Includes/propTypeValidators';
import CompanyLogo from '../CompanyLogo/CompanyLogo';
import { googleAnalytic } from '../../../../pages/googeAnalyticHelper';
import Pixel, {
    EmitMetric,
} from '../../../Analytics/VisibilityPixel/VisibilityPixel';
import JobLikeReaction from '../JobLikeReaction/JobLikeReaction';
import {
    buildMetrics,
    calculateIsNewJob,
    calculateIsActivelyHiring,
} from './constants';
import JobTagBar from '../JobTagBar/JobTagBar';
import { ellipsify } from '../../../../utilities/helperFunctions';
import { handleFiringSearchEmitterOnSearchResult } from '../../searchEmitters';

import './JobListing.scss';

export const JobListing = ({
    hash,
    companyLogo,
    title,
    company,
    location,
    isRemote,
    active,
    jobPageUrl,
    daysSinceCreation,
    showExpired,
    category,
    originalApplyUrl,
    onClick,
    hideReactions,
    searchTracking,
    ctsName,
}) => {
    const isExpired = showExpired && !active;
    const isNewJob = calculateIsNewJob(daysSinceCreation);
    const isActivelyHiring = calculateIsActivelyHiring(company.jobCount);
    const queryID = searchTracking?.queryID;
    const position = searchTracking?.positionInListing;

    const handleJobClick = useCallback(() => {
        const jobClickMetric = buildMetrics({
            type: 'click',
            originalApplyUrl,
            category,
            company,
            hash,
            title,
            location,
            jobSearchQueryID: queryID,
            position,
            miscEventType: null,
            ctsName,
            ctsEvent: 'VIEW',
        });

        const isSearchQueryTracking =
            searchTracking?.positionInListing &&
            searchTracking?.queryID &&
            searchTracking?.indexName &&
            ctsName;

        if (isSearchQueryTracking) {
            handleFiringSearchEmitterOnSearchResult({
                queryID,
                hash,
                ctsName,
            });
        }

        // @TODO: remove usage - fires an event for directly clicking on job (not related to search), maybe implement something similar for Google CTS
        // if (!isSearchQueryTracking) {
        //     handleFiringNonSearchAlgoliaEmitter(
        //         hash,
        //         '',
        //         'Clicked on job listing from saved jobs'
        //     );
        // }

        // fire click metric
        EmitMetric(jobClickMetric);

        // handle google analytics
        googleAnalytic('Click', 'JobsV3', company.name);

        if (onClick) return onClick();
    }, [
        originalApplyUrl,
        company,
        category,
        hash,
        title,
        location,
        onClick,
        searchTracking,
        ctsName,
        position,
        queryID,
    ]);

    const pixelMetrics = buildMetrics({
        type: 'impression',
        originalApplyUrl,
        category,
        company,
        hash,
        title,
        location,
        jobSearchQueryID: queryID,
        position,
        miscEventType: null,
        ctsName,
        ctsEvent: 'IMPRESSION',
    });

    const reactionBar = !hideReactions ? <JobLikeReaction hash={hash} /> : null;

    return (
        <>
            <div className="job-listing-separator" />
            <div className="component-JobV3-Elements-JobListing">
                <div className="logo-container">
                    <CompanyLogo
                        src={companyLogo.src}
                        alt={companyLogo.alt}
                        href={company.url}
                    />
                </div>
                <div className="job-content">
                    <Pixel metrics={pixelMetrics} />
                    <JobTagBar
                        isExpired={isExpired}
                        isNewJob={isNewJob}
                        isActivelyHiring={isActivelyHiring}
                        isRemote={isRemote}
                    />
                    <a
                        className="job-title"
                        href={jobPageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={handleJobClick}
                    >
                        {ellipsify(title, 50, true)}
                    </a>
                    <div className="company-details">
                        <a
                            className="company-name"
                            href={company.url}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {company.name}
                        </a>
                        <span className="separator">|</span>
                        <span className="company-location">{location}</span>
                    </div>
                </div>
                <div className="job-reactions">{reactionBar}</div>
            </div>
        </>
    );
};

JobListing.propTypes = {
    hash: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    company: PropTypes.shape({
        name: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        jobCount: nullable(PropTypes.number.isRequired),
    }).isRequired,
    companyLogo: PropTypes.exact({
        src: PropTypes.string.isRequired,
        alt: PropTypes.string,
    }),
    category: PropTypes.string,
    jobPageUrl: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    originalApplyUrl: PropTypes.string.isRequired,
    daysSinceCreation: PropTypes.number,
    onClick: PropTypes.func,
    showExpired: PropTypes.bool,
    isRemote: PropTypes.bool,
    active: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    hideReactions: PropTypes.bool,
    searchTracking: PropTypes.shape({
        positionInListing: PropTypes.number,
        queryID: PropTypes.string,
        indexName: PropTypes.string,
    }),
    ctsName: PropTypes.string,
};

JobListing.defaultProps = {
    companyLogo: {
        src: '/v1/icons/company_2x.png',
        alt: 'company-logo',
    },
    onClick: null,
    category: null,
    showExpired: false,
    daysSinceCreation: null,
    isRemote: false,
    active: true,
    hideReactions: false,
    searchTracking: null,
    ctsName: '',
};

JobListing.displayName = 'JobListing';

export default memo(JobListing);
